import { useEffect, useMemo, useRef, useState } from 'react';
import { Building2, Check, Download, FolderKanban, GraduationCap, Link, Search, Send, Smartphone, Trash2, Upload, Users, Video, X } from 'lucide-react';
import { api } from '../../lib/api';
import { useI18n } from '../../lib/i18n';
import { fmtElapsed } from '../../lib/share/peer';
import type { TargetRow, TargetsResponse } from '../../lib/share/types';
import { fmtRemaining } from '../../lib/rec/uploader';
import { claimLabel } from '../../lib/rec/types';
import { Avatar } from '../Avatar';
import { RecordingPlayer } from './RecordingPlayer';
import { useRec } from './RecProvider';
import { copiarAlPortapapeles } from '../../lib/portapapeles';
import { SendToTrainingPanel } from './SendToTraining';

/**
 * LA VISTA PREVIA tras Stop (spec §4.12.1 punto 6): el Blob local se ve al instante; título
 * editable; duración; barra "Uploading a/b… about {t} left"; Send to… (compañeros, inspectores si
 * `/targets` los trae); Attach to claim (buscador + canal, nunca `contractor`); Copy link;
 * Download (del Blob local); Delete. Se puede enviar antes del remux (la tarjeta dirá
 * "Processing…"), pero no antes de que la subida termine (`status` ≥ uploaded).
 */
type Panel = 'none' | 'send' | 'attach' | 'training';

export function RecordPreview({ ext }: { ext: 'mp4' | 'webm' }) {
  const { t } = useI18n();
  const r = useRec();
  const p = r.preview!;
  const u = r.upload;
  const [panel, setPanel] = useState<Panel>('none');
  const [title, setTitle] = useState(p.title);
  const titleRef = useRef(p.title);
  useEffect(() => { setTitle(p.title); titleRef.current = p.title; }, [p.title]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && panel === 'none') r.closePreview(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [panel, r]);

  const uploaded = p.status === 'uploaded';
  const uploading = p.status === 'uploading' || p.status === 'completing';
  // Una grabación vive en UN claim (el backend contesta 409 al segundo): el botón se apaga.
  const attached = p.recording?.claimId != null;
  const total = Math.max(u?.partsQueued ?? 0, 1);
  const done = u?.partsDone ?? 0;
  const pct = uploaded ? 100 : Math.min(99, Math.round((done / total) * 100));
  const link = p.id != null ? `${window.location.origin}/rec/${p.id}` : null;

  const commitTitle = () => {
    const clean = title.trim();
    if (clean && clean !== titleRef.current) { titleRef.current = clean; r.setTitle(clean); }
    else if (!clean) setTitle(titleRef.current);
  };
  const copy = async () => {
    if (!link) return;
    try { await copiarAlPortapapeles(link); r.notify({ kind: 'linkCopied' }); }
    catch { window.prompt(t('rec.copyLink'), link); }
  };
  const fileName = `${(title.trim() || 'recording').replace(/[\\/:*?"<>|]+/g, '-')}.${ext}`;

  return (
    <div className="ash-modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget && panel === 'none') r.closePreview(); }}>
      <div className="ash-modal rec-preview" role="dialog" aria-modal="true" aria-label={t('rec.previewTitle')}>
        <div className="ash-modal-head">
          <b style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><Video size={17} strokeWidth={1.9} />{t('rec.previewTitle')}</b>
          <button type="button" className="btn ghost sm ash-open" onClick={r.closePreview} aria-label={t('common.close')}><X size={15} strokeWidth={1.75} /></button>
        </div>

        <RecordingPlayer id={p.id} mediaUrl={p.blobUrl} title={title} local hideOpenWindow hideDownload />

        <div className="rec-preview-meta">
          <input className="input sm rec-title" value={title} placeholder={t('rec.titlePh')} maxLength={140}
            onChange={(e) => setTitle(e.target.value)} onBlur={commitTitle} onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }} />
          <span className="ss-mono rec-preview-dur">{fmtElapsed(p.durationMs)}</span>
        </div>

        {/* Subida: partes, estimado, reintento */}
        <div className={`rec-upload${p.status === 'failed' ? ' failed' : uploaded ? ' done' : ''}`} role="status">
          <div className="rec-upload-bar" aria-hidden><div className="rec-upload-fill" style={{ width: `${pct}%` }} /></div>
          <div className="rec-upload-text">
            {p.status === 'failed' ? (
              <>
                <span>{t('rec.failedUpload')}</span>
                <button type="button" className="btn ghost sm" onClick={r.retryUpload}><Upload size={13} strokeWidth={2} /> {t('rec.retryUpload')}</button>
              </>
            ) : uploaded ? (
              <span><Check size={13} strokeWidth={2.5} aria-hidden /> {p.recording && p.recording.status !== 'ready' ? t('rec.uploadedProcessing') : t('rec.uploaded')}</span>
            ) : (
              <>
                <span>{p.status === 'completing' ? t('rec.finalizing') : t('rec.uploadingParts', { a: done, b: total })}</span>
                {u?.offline && <span className="warn">{t('rec.uploadPaused')}</span>}
                {!u?.offline && u?.remainingSec != null && u.remainingSec > 0 && <span className="muted">{t('rec.remaining', { t: fmtRemaining(u.remainingSec) })}</span>}
              </>
            )}
          </div>
        </div>

        {/* Acciones */}
        <div className="rec-preview-actions">
          <button type="button" className={`btn sm${panel === 'send' ? ' on' : ''}`} disabled={!uploaded} title={!uploaded ? t('rec.waitUpload') : undefined} onClick={() => setPanel(panel === 'send' ? 'none' : 'send')}>
            <Send size={14} strokeWidth={2} /> {t('rec.sendTo')}
          </button>
          <button type="button" className={`btn ghost sm${panel === 'attach' ? ' on' : ''}`} disabled={!uploaded || attached} title={!uploaded ? t('rec.waitUpload') : attached ? t('rec.alreadyAttached') : undefined} onClick={() => setPanel(panel === 'attach' ? 'none' : 'attach')}>
            <FolderKanban size={14} strokeWidth={1.75} /> {t('rec.attach')}
          </button>
          {/* Training (mig 0472): cae al inbox de Training y un super-manager lo acomoda. */}
          <button type="button" className={`btn ghost sm${panel === 'training' ? ' on' : ''}`} disabled={!uploaded || p.id == null} title={!uploaded ? t('rec.waitUpload') : undefined} onClick={() => setPanel(panel === 'training' ? 'none' : 'training')}>
            <GraduationCap size={14} strokeWidth={1.75} /> {t('trn.sendToTraining')}
          </button>
          <button type="button" className="btn ghost sm" disabled={!link || !uploaded} onClick={() => { void copy(); }}><Link size={14} strokeWidth={1.75} /> {t('rec.copyLink')}</button>
          {p.blobUrl && <a className="btn ghost sm" href={p.blobUrl} download={fileName}><Download size={14} strokeWidth={1.75} /> {t('rec.download')}</a>}
          <span style={{ flex: 1 }} />
          <button type="button" className="btn ghost sm rec-del" disabled={uploading} onClick={() => { void r.deleteCurrent(); }}><Trash2 size={14} strokeWidth={1.75} /> {t('rec.delete')}</button>
        </div>

        {panel === 'send' && <SendPanel onDone={() => setPanel('none')} />}
        {panel === 'attach' && <AttachPanel onDone={() => setPanel('none')} />}
        {panel === 'training' && p.id != null && <SendToTrainingPanel recordingId={p.id} onDone={() => setPanel('none')} />}
        {p.recording && (p.recording.sentTo > 0 || p.recording.claimId != null) && (
          <div className="rec-preview-sent muted">
            {p.recording.sentTo > 0 && <span><Users size={12} strokeWidth={2} aria-hidden /> {t('rec.sentTo', { n: p.recording.sentTo })}</span>}
            {p.recording.claim && <a href={`/claims/${p.recording.claim.id}`} target="_blank" rel="noreferrer"><FolderKanban size={12} strokeWidth={2} aria-hidden /> {t('rec.attached', { claim: claimLabel(p.recording.claim) })}</a>}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * "Send to…": PERSONAS (los mismos compañeros del selector del share, `/api/share/targets`, con los
 * inspectores si el flag los trae) Y DEPARTAMENTOS (pedido del dueño 2026-09-10: "enviárselo a quien
 * yo quiera incluyendo por departamentos"), ambos en multiselección. El backend expande cada
 * departamento a sus miembros activos y no duplica a quien ya iba por persona. Si el share está
 * apagado y Record prendido, las personas caen a `/api/messaging/users`.
 */
function SendPanel({ onDone }: { onDone: () => void }) {
  const { t } = useI18n();
  const r = useRec();
  const [rows, setRows] = useState<TargetRow[] | null>(null);
  const [depts, setDepts] = useState<Dept[]>([]);
  const [q, setQ] = useState('');
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [selDepts, setSelDepts] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let alive = true;
    api.get<TargetsResponse>('/api/share/targets')
      .then((res) => { if (alive) setRows(res.rows); })
      .catch(() => api.get<{ users: Array<{ id: string; name: string; role: string }> }>('/api/messaging/users')
        .then((res) => { if (alive) setRows(res.users.map((x) => ({ id: x.id, name: x.name, role: x.role, department: null, appOpen: false, humanRecent: false, busy: null, group: 'everyone', phone: x.role === 'inspector', onJob: false }))); })
        .catch(() => { if (alive) setRows([]); }));
    // Los departamentos del tenant, NUNCA `contractor` (una grabación jamás se manda al contratista).
    api.get<{ departments: Dept[] }>('/api/departments').then((res) => { if (alive) setDepts(res.departments.filter((d) => d.key !== 'contractor')); }).catch(() => { if (alive) setDepts([]); });
    inputRef.current?.focus();
    return () => { alive = false; };
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const list = (rows ?? []).filter((x) => !needle || x.name.toLowerCase().includes(needle));
    return [...list].sort((a, b) => Number(b.appOpen) - Number(a.appOpen) || a.name.localeCompare(b.name));
  }, [q, rows]);
  const filteredDepts = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return depts.filter((d) => !needle || d.name.toLowerCase().includes(needle));
  }, [q, depts]);

  const toggle = (id: string) => setSel((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleDept = (key: string) => setSelDepts((s) => { const n = new Set(s); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  const totalSel = sel.size + selDepts.size;
  const send = async () => {
    if (!totalSel) return;
    setBusy(true);
    try { await r.sendToUsers([...sel], [...selDepts]); onDone(); }
    catch { /* el aviso ya salió */ }
    finally { setBusy(false); }
  };

  return (
    <div className="rec-panel">
      <div className="rec-panel-head"><b>{t('rec.pickPeople')}</b><button type="button" className="btn ghost sm ash-open" onClick={onDone} aria-label={t('common.close')}><X size={14} strokeWidth={1.75} /></button></div>
      <div className="ss-search">
        <Search size={15} strokeWidth={2} className="ss-search-ico" />
        <input ref={inputRef} className="input sm ss-search-input" placeholder={t('share.search')} value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      <div className="ss-list rec-people">
        {/* DEPARTAMENTOS primero (mandar a un equipo entero es lo nuevo del pedido). */}
        {filteredDepts.length > 0 && <div className="rec-send-group">{t('rec.departments')}</div>}
        {filteredDepts.map((d) => (
          <label key={`dept:${d.key}`} className={`ss-row rec-person${selDepts.has(d.key) ? ' is-sel' : ''}`}>
            <input type="checkbox" checked={selDepts.has(d.key)} onChange={() => toggleDept(d.key)} />
            <span className="rec-dept-ico" aria-hidden><Building2 size={16} strokeWidth={1.9} /></span>
            <span className="ss-row-main">
              <span className="ss-row-name">{d.name}</span>
              <span className="ss-row-sub">{t('rec.wholeDept')}</span>
            </span>
          </label>
        ))}
        {rows === null && <div className="ss-empty">{t('common.loading')}</div>}
        {rows !== null && !filtered.length && !filteredDepts.length && <div className="ss-empty">{t('rec.nobody')}</div>}
        {filtered.length > 0 && <div className="rec-send-group">{t('rec.people')}</div>}
        {filtered.map((x) => (
          <label key={x.id} className={`ss-row rec-person${sel.has(x.id) ? ' is-sel' : ''}`}>
            <input type="checkbox" checked={sel.has(x.id)} onChange={() => toggle(x.id)} />
            <Avatar userId={x.id} name={x.name} size={22} noCall />
            <span className="ss-row-main">
              <span className="ss-row-name">{x.name}</span>
              <span className="ss-row-sub">{x.role}{x.department ? ` · ${x.department}` : ''}</span>
            </span>
            {x.phone && <span className="ss-row-hint"><Smartphone size={11} strokeWidth={2} aria-hidden /></span>}
          </label>
        ))}
      </div>
      <div className="rec-panel-actions">
        <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{totalSel ? t('rec.selectedN', { n: totalSel }) : ''}</span>
        <button type="button" className="btn sm" disabled={!totalSel || busy} onClick={() => { void send(); }}><Send size={14} strokeWidth={2} /> {t('rec.send')}</button>
      </div>
    </div>
  );
}

type ClaimHit = { id: string; claim_number: string | null; homeowner_name: string | null; address: string | null; current_status: string | null };
type Dept = { key: string; name: string };

/** "Attach to claim": buscador de claims (`/api/search`) + canal (nunca `contractor`) + mensaje opcional. */
function AttachPanel({ onDone }: { onDone: () => void }) {
  const { t } = useI18n();
  const r = useRec();
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<ClaimHit[]>([]);
  const [searching, setSearching] = useState(false);
  const [claim, setClaim] = useState<ClaimHit | null>(null);
  const [depts, setDepts] = useState<Dept[]>([]);
  const [dept, setDept] = useState<string>('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    api.get<{ departments: Dept[] }>('/api/departments').then((res) => setDepts(res.departments.filter((d) => d.key !== 'contractor'))).catch(() => setDepts([]));
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setHits([]); setSearching(false); return undefined; }
    setSearching(true);
    const id = window.setTimeout(() => {
      let alive = true;
      api.get<{ claims: ClaimHit[] }>(`/api/search?q=${encodeURIComponent(term)}`)
        .then((res) => { if (alive) setHits(res.claims.slice(0, 12)); })
        .catch(() => { if (alive) setHits([]); })
        .finally(() => { if (alive) setSearching(false); });
      return () => { alive = false; };
    }, 280);
    return () => clearTimeout(id);
  }, [q]);

  const attach = async () => {
    if (!claim) return;
    setBusy(true);
    try { await r.attachToClaim(Number(claim.id), dept || null, body, label(claim) || `#${claim.id}`); onDone(); }
    catch { /* el aviso ya salió */ }
    finally { setBusy(false); }
  };
  const label = (c: ClaimHit) => [c.claim_number ? `#${c.claim_number}` : null, c.homeowner_name, c.address].filter(Boolean).join(' · ');

  return (
    <div className="rec-panel">
      <div className="rec-panel-head"><b>{t('rec.attach')}</b><button type="button" className="btn ghost sm ash-open" onClick={onDone} aria-label={t('common.close')}><X size={14} strokeWidth={1.75} /></button></div>
      {!claim ? (
        <>
          <div className="ss-search">
            <Search size={15} strokeWidth={2} className="ss-search-ico" />
            <input ref={inputRef} className="input sm ss-search-input" placeholder={t('rec.searchClaim')} value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <div className="ss-list rec-claims">
            {searching && <div className="ss-empty">{t('common.loading')}</div>}
            {!searching && q.trim().length >= 2 && !hits.length && <div className="ss-empty">{t('rec.nobody')}</div>}
            {hits.map((c) => (
              <button type="button" key={c.id} className="ss-row" onClick={() => setClaim(c)}>
                <FolderKanban size={16} strokeWidth={1.75} aria-hidden />
                <span className="ss-row-main">
                  <span className="ss-row-name">{label(c) || `#${c.id}`}</span>
                  {c.current_status && <span className="ss-row-sub">{c.current_status}</span>}
                </span>
              </button>
            ))}
          </div>
        </>
      ) : (
        <div className="rec-attach-form">
          <div className="rec-attach-claim"><FolderKanban size={14} strokeWidth={1.75} aria-hidden /> <b>{label(claim) || `#${claim.id}`}</b> <button type="button" className="btn ghost sm" onClick={() => setClaim(null)}>{t('common.change')}</button></div>
          <label className="rec-attach-row">
            <span>{t('rec.channel')}</span>
            <select className="input sm" value={dept} onChange={(e) => setDept(e.target.value)}>
              <option value="">{t('rec.channelGeneral')}</option>
              {depts.map((d) => <option key={d.key} value={d.key}>{d.name}</option>)}
            </select>
          </label>
          <p className="ss-hint" style={{ margin: 0 }}>{t('rec.contractorNo')}</p>
          <textarea className="input sm" rows={2} placeholder={t('rec.attachNote')} value={body} onChange={(e) => setBody(e.target.value)} />
          <div className="rec-panel-actions">
            <button type="button" className="btn sm" disabled={busy} onClick={() => { void attach(); }}><FolderKanban size={14} strokeWidth={1.75} /> {t('rec.attach')}</button>
          </div>
        </div>
      )}
    </div>
  );
}
